import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CurrentClientContext from '../../providers/CurrentClientProvider';
import UserService from '../../services/UserService';
import TipsContext from '../../providers/GlobalTip';
import PerformanceDashboardService from '../../services/dashboards/PerformanceDashboard.service';
import EpochMonthService from '../../services/EpochMonth.service';
import { ClientLevelItem, CategoryLevelItem, LocationLevelItem, ClassLevelItem } from '../../interfaces/dashboards/PerformanceDashboard.interface';
import MultiSelect from '../ui/MultiSelect';
import { InventoryActionReport } from '../reports/inventoryAction/InventoryActionTableReport.component';
import { Button, Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import ReportService from '../../services/reports/Report.service';

interface Client {
    Id: number;
    Name: string;
    ClientCode: string;
}

interface CurrentClientContextType {
    client?: Client;
    setClient?: (client: Client | undefined) => void;
}

interface TipsContextType {
    show: (message: string) => void;
}

type ValueMode = 'retail' | 'cost';
type PeriodView = 'current' | 'last';
type InventoryView = 'buying' | 'fastest' | 'slowest';

interface DropdownOption {
    code: string;
    name: string;
    label: string;
    category?: string;
    sortOrder?: number;
}

interface InventoryRow {
    class: string;
    sales: string;
    markdowns: string;
    inventory: string;
    invTrend: string;
    invPlan: string;
    inflowTrend: string;
    onOrder: string;
    otb: string;
}

interface ClientProgressProps {
    onToggleBack?: () => void;
}

const ClientProgress: React.FC<ClientProgressProps> = ({ onToggleBack }) => {
    const navigate = useNavigate();
    const [forceProgress, setForceProgress] = useState<boolean>(false);
    const currentClientContext = CurrentClientContext.useClient() as CurrentClientContextType;
    const [hasMultipleClients, setHasMultipleClients] = useState<boolean>(false);
    const tip_context = TipsContext.useMessage() as TipsContextType;
    const [valueMode, setValueMode] = useState<ValueMode>('cost');
    const [otbValueMode, setOtbValueMode] = useState<ValueMode>('cost');

    // Period view: default to "last" for first 10 days of month, "current" otherwise
    const getDefaultPeriodView = (): PeriodView => {
        const dayOfMonth = new Date().getDate();
        return dayOfMonth <= 10 ? 'last' : 'current';
    };
    const [periodView, setPeriodView] = useState<PeriodView>(getDefaultPeriodView());

    const [inventoryView, setInventoryView] = useState<InventoryView>('buying');
    const [endingPeriod, setEndingPeriod] = useState<number>(EpochMonthService.getTodayEpochMonth());
    const [beginningPeriod, setBeginningPeriod] = useState<number>(EpochMonthService.getTodayEpochMonth());
    const [trendPercent, setTrendPercent] = useState<number>(0);
    const [trendPercentInput, setTrendPercentInput] = useState<string>('0.00');

    // Dashboard data state
    const [clientLevelData, setClientLevelData] = useState<ClientLevelItem[] | null>(null);
    const [classLevelData, setClassLevelData] = useState<ClassLevelItem[] | null>(null);
    const [thisMonthClientLevelData, setThisMonthClientLevelData] = useState<ClientLevelItem | null>(null);
    const [lastMonthClientLevelData, setLastMonthClientLevelData] = useState<ClientLevelItem | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [epochMonth, setEpochMonth] = useState<number>(EpochMonthService.getTodayEpochMonth());
    const [compareEpochMonth, setCompareEpochMonth] = useState<number>(EpochMonthService.getTodayEpochMonth() - 1);

    // Dropdown options and selections
    const [locationOptions, setLocationOptions] = useState<DropdownOption[]>([]);
    const [categoryOptions, setCategoryOptions] = useState<DropdownOption[]>([]);
    const [classOptions, setClassOptions] = useState<DropdownOption[]>([]);
    //todo: selected locations displaying odd behavior 
    const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
    const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
    const [selectedClasses, setSelectedClasses] = useState<string[]>([]);

    // Inventory Health data
    const [inventoryHealthData, setInventoryHealthData] = useState<any[]>([]);
    const [isLoadingInventoryHealth, setIsLoadingInventoryHealth] = useState<boolean>(false);
    const [tableLimit, setTableLimit] = useState<number>(5); // Default to Top 5 Items

    // Inventory Action Report modal
    const [showInventoryActionReport, setShowInventoryActionReport] = useState<boolean>(false);

    // Generate ending period options (current month + 12 months forward)
    const endingPeriodOptions = React.useMemo(() => {
        const currentEpoch = EpochMonthService.getTodayEpochMonth();
        const options = [];

        for (let i = 0; i <= 12; i++) {
            const epochMonth = currentEpoch + i;
            // Calculate year and month from epoch
            const yearsSince1970 = Math.floor(epochMonth / 12);
            const monthIndex = epochMonth % 12;
            const year = 1970 + yearsSince1970;
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                              'July', 'August', 'September', 'October', 'November', 'December'];
            const label = `${monthNames[monthIndex]} ${year}`;
            options.push({ value: epochMonth, label });
        }

        return options;
    }, []);

    // Generate beginning period options (current month back 11 months for total of 12 months)
    const beginningPeriodOptions = React.useMemo(() => {
        const currentEpoch = EpochMonthService.getTodayEpochMonth();
        const options = [];

        // Go back 11 months from current (total of 12 months including current)
        for (let i = 11; i >= 0; i--) {
            const epochMonth = currentEpoch - i;
            // Calculate year and month from epoch
            const yearsSince1970 = Math.floor(epochMonth / 12);
            const monthIndex = epochMonth % 12;
            const year = 1970 + yearsSince1970;
            const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                              'July', 'August', 'September', 'October', 'November', 'December'];
            const label = `${monthNames[monthIndex]} ${year}`;
            options.push({ value: epochMonth, label });
        }

        return options;
    }, []);

    // Get formatted label for current ending period
    const endingPeriodLabel = React.useMemo(() => {
        const option = endingPeriodOptions.find(opt => opt.value === endingPeriod);
        return option ? option.label : 'Unknown';
    }, [endingPeriod, endingPeriodOptions]);

    // Get current month abbreviation
    const currentMonthAbbr = React.useMemo(() => {
        const monthIndex = epochMonth % 12;
        const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthAbbreviations[monthIndex];
    }, [epochMonth]);

    // Get next month abbreviation for inventory plan
    const nextMonthAbbr = React.useMemo(() => {
        const nextMonthIndex = (epochMonth + 1) % 12;
        const monthAbbreviations = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return monthAbbreviations[nextMonthIndex];
    }, [epochMonth]);

    useEffect(() => {
        UserService.getUserClients().then(async (res: Client[]) => {

            if (res && res.length > 1) {
                setHasMultipleClients(true);
            }
        });
    }, []);

    useEffect(() => {
        if (!hasMultipleClients) {
            return;
        }
    }, [currentClientContext.client?.Id, hasMultipleClients]);

    // Memoize filter array strings for stable useEffect dependencies
    const locationsKey = React.useMemo(() => selectedLocations.join(','), [selectedLocations]);
    const categoriesKey = React.useMemo(() => selectedCategories.join(','), [selectedCategories]);
    const classesKey = React.useMemo(() => selectedClasses.join(','), [selectedClasses]);

    // NOTE: periodView (Current/Last Month toggle) only affects the Monthly Performance card display
    // It does NOT change epochMonth or reload data. Merchandise Insights always uses current month data.

    // Load dropdown options (locations, categories, classes)
    useEffect(() => {
        if (!currentClientContext.client?.Id) {
            return;
        }

        const loadDropdownOptions = async () => {
            try {
                const data = await PerformanceDashboardService.getDropdownOptions({
                    clientId: currentClientContext.client!.Id,
                    epochMonth: epochMonth
                });

                if (data) {
                    setLocationOptions(data.locations || []);
                    //todo: double check category filters since sql doesnt have the same field count
                    setCategoryOptions(data.categories || []);
                    setClassOptions(data.classes || []);
                }
            } catch (error) {
                console.error('Error loading dropdown options:', error);
                tip_context.show('Error loading dropdown options');
            }
        };

        loadDropdownOptions();
    }, [currentClientContext.client?.Id, epochMonth]);

    // Load dashboard data
    useEffect(() => {
        if (!currentClientContext.client?.Id) {
            return;
        }

        const loadDashboardData = async () => {
            setIsLoading(true);
            try {
                const data = await PerformanceDashboardService.getSimpleDashboardDataForClient({
                    clientId: currentClientContext.client!.Id,
                    epochMonth: epochMonth,
                    compareEpochMonth: compareEpochMonth,
                    locations: selectedLocations,
                    categories: selectedCategories,
                    classes: selectedClasses
                });

                if (data && data.client) {
                    setClientLevelData(data.client);
                    setClassLevelData(data.classes || null);

                    // Find current and last month data
                    const thisMonth = data.client.find((item: ClientLevelItem) => item.epochMonthSpan === epochMonth);
                    const lastMonth = data.client.find((item: ClientLevelItem) => item.epochMonthSpan === compareEpochMonth);

                    setThisMonthClientLevelData(thisMonth || null);
                    setLastMonthClientLevelData(lastMonth || null);

                    // Initialize trend percent from max trend in current month data
                    if (data.client.length > 0) {
                        const currentMonthData = data.client.filter((item: ClientLevelItem) => item.epochMonthSpan === epochMonth);
                        if (currentMonthData.length > 0) {
                            const maxTrend = Math.max(...currentMonthData.map((item: ClientLevelItem) => item.trend || 0));
                            // Convert from decimal to percentage (e.g., 0.0322 -> 3.22)
                            setTrendPercent(maxTrend * 100);
                        }
                    }
                }
            } catch (error) {
                console.error('Error loading dashboard data:', error);
                tip_context.show('Error loading dashboard data');
            } finally {
                setIsLoading(false);
            }
        };

        loadDashboardData();
    }, [currentClientContext.client?.Id, epochMonth, compareEpochMonth, locationsKey, categoriesKey, classesKey]);

    useEffect(() => {
        setTrendPercentInput(trendPercent.toFixed(2));
    }, [trendPercent]);

    // Load inventory health data
    useEffect(() => {
        if (!currentClientContext.client?.Id) {
            return;
        }

        const loadInventoryHealth = async () => {
            setIsLoadingInventoryHealth(true);
            try {
                let data;

                // Debug: Log filter values
                console.log('Loading inventory health with filters:', {
                    selectedLocations,
                    selectedCategories,
                    selectedClasses,
                    inventoryView
                });

                if (inventoryView === 'buying') {
                    // Load buying opportunity data
                    data = await PerformanceDashboardService.getInventoryHealthData({
                        clientId: currentClientContext.client!.Id,
                        epochMonth: epochMonth,
                        compareEpochMonth: compareEpochMonth,
                        locations: selectedLocations,
                        categories: selectedCategories,
                        classes: selectedClasses,
                        limit: tableLimit,
                        endingPeriod: endingPeriod
                    });
                } else if (inventoryView === 'fastest') {
                    // Load fastest sell-through data
                    data = await PerformanceDashboardService.getFastestSellThroughData({
                        clientId: currentClientContext.client!.Id,
                        epochMonth: epochMonth,
                        beginningPeriod: beginningPeriod,
                        compareEpochMonth: epochMonth - 12, // Last year same month
                        locations: selectedLocations,
                        categories: selectedCategories,
                        classes: selectedClasses,
                        limit: tableLimit
                    });
                } else if (inventoryView === 'slowest') {
                    // Load slowest sell-through data
                    data = await PerformanceDashboardService.getSlowestSellThroughData({
                        clientId: currentClientContext.client!.Id,
                        epochMonth: epochMonth,
                        beginningPeriod: beginningPeriod,
                        compareEpochMonth: epochMonth - 12, // Last year same month
                        locations: selectedLocations,
                        categories: selectedCategories,
                        classes: selectedClasses,
                        limit: tableLimit
                    });
                }

                if (data) {
                    setInventoryHealthData(data);
                }
            } catch (error) {
                console.error('Error loading inventory health data:', error);
                tip_context.show('Error loading inventory health data');
            } finally {
                setIsLoadingInventoryHealth(false);
            }
        };

        loadInventoryHealth();
    }, [currentClientContext.client?.Id, epochMonth, compareEpochMonth, locationsKey, categoriesKey, classesKey, inventoryView, beginningPeriod, tableLimit, endingPeriod]);

    // Helper function to get filtered class options based on selected categories
    const getFilteredClassOptions = () => {
        // If no categories selected OR all categories selected (empty array), show all classes
        if (selectedCategories.length === 0 || selectedCategories.length === categoryOptions.length) {
            return classOptions;
        }
        return classOptions.filter(classOption =>
            classOption.category && selectedCategories.includes(classOption.category)
        );
    };


    // Computed OTB values
    // Current month uses OTB Trend = Inventory Trend - onOrder
    // Future months use regular OTB values summed
    const computedOtbValues = React.useMemo(() => {
        if (!clientLevelData || clientLevelData.length === 0) {
            return { retail: 0, cost: 0 };
        }

        const trendDecimal = trendPercent / 100;

        // Find current month data
        const currentMonthData = clientLevelData.find(item => item.epochMonthSpan === epochMonth);

        // Calculate OTB Trend for current month
        let currentMonthOtbRetail = 0;
        let currentMonthOtbCost = 0;

        // If user is viewing last month, do NOT use the trended OTB formula.
        // Instead use: OTB EOM - Inventory Deviation (retail/cost), for the displayed (last) month.
        if (periodView === 'last') {
            if (currentMonthData) {
                currentMonthOtbRetail = (currentMonthData.otbRetailEom || 0) + (currentMonthData.inventoryDeviationRm || 0);
                currentMonthOtbCost = (currentMonthData.otbCostEom || 0) + (currentMonthData.inventoryDeviationCostRm || 0);
            }
        } else if (trendDecimal > 0) {
            // For current month (periodView !== 'last'), calculate trended OTB at CLASS level and sum.
            // This improves cost accuracy by using per-class cost complements.
            const currentMonthClasses = (classLevelData || []).filter(c => c.epochMonthRm === epochMonth);

            if (currentMonthClasses.length > 0) {
                let retailSum = 0;
                let costSum = 0;

                for (const cls of currentMonthClasses) {
                    const otbLessTrend = (cls as ClassLevelItem).otbLessTrend ?? 0;
                    const sales = (cls as ClassLevelItem).salesRm ?? 0;
                    const onOrderRetailMTD = (cls as ClassLevelItem).onOrderRetailMTD ?? 0;
                    const onOrderCostMTD = (cls as ClassLevelItem).onOrderCostMTD ?? 0;
                    const costComplementMtd = (cls as ClassLevelItem).costComplementMtd || 1;

                    const salesTrend = (sales / trendDecimal)
                    // otb less trend is the inflow trend formula without adding the salesTrend (so that most of the calc is handled in sql)
                    let retail = otbLessTrend + salesTrend - onOrderRetailMTD;

                    retailSum += retail;

                    costSum += (retail + onOrderRetailMTD) * costComplementMtd - onOrderCostMTD;
                }

                currentMonthOtbRetail = retailSum;
                currentMonthOtbCost = costSum;
            }
        }

        // Filter data for future months (> epochMonth and <= endingPeriod)
        const futureMonths = clientLevelData.filter(item =>
            item.epochMonthSpan > epochMonth && item.epochMonthSpan <= endingPeriod
        );

        // Sum regular OTB values for future months
        const futureOtbRetail = futureMonths.reduce((sum, item) =>
            sum + (item.otbRetailMtd || 0), 0
        );

        const futureOtbCost = futureMonths.reduce((sum, item) =>
            sum + (item.otbCostMtd || 0), 0
        );

        return {
            retail: currentMonthOtbRetail + futureOtbRetail,
            cost: currentMonthOtbCost + futureOtbCost
        };
    }, [clientLevelData, classLevelData, epochMonth, endingPeriod, trendPercent, periodView, compareEpochMonth]);

    // Computed Inventory Trend for Current Month
    // Formula: inventoryBomPlanNM - (inventoryCurrent - ((salesRM / trend - salesRM) + markdownsRemaining)) + onOrderRetailMTD
    // todo: adjust trend on inventory to match pod
    const computedInventoryTrend = React.useMemo(() => {
        if (!thisMonthClientLevelData) {
            return 0;
        }

        const trendDecimal = trendPercent / 100;
        const projectedSales = trendDecimal > 0
            ? (thisMonthClientLevelData.salesRm / trendDecimal) - thisMonthClientLevelData.salesRm
            : 0;

        return (thisMonthClientLevelData.inventoryBomPlanNM || 0) -
            ((thisMonthClientLevelData.inventoryCurrent || 0) -
            (projectedSales + (thisMonthClientLevelData.markdownsRemaining || 0))) +
            (thisMonthClientLevelData.onOrderRetailMTD || 0);
    }, [thisMonthClientLevelData, trendPercent]);

    // Computed Inflow Trend for Current Month
    // Formula: inventoryBomPlanNM - (inventoryCurrent - ((salesRM / trend - salesRM) + markdownsRemaining))
    // Same as inventory trend but WITHOUT onOrderRetailMTD
    const computedInflowTrend = React.useMemo(() => {
        if (!thisMonthClientLevelData) {
            return 0;
        }

        const trendDecimal = trendPercent / 100;
        const projectedSales = trendDecimal > 0
            ? (thisMonthClientLevelData.salesRm / trendDecimal) - thisMonthClientLevelData.salesRm
            : 0;

        return (thisMonthClientLevelData.inventoryBomPlanNM || 0) -
            ((thisMonthClientLevelData.inventoryCurrent || 0) -
            (projectedSales + (thisMonthClientLevelData.markdownsRemaining || 0)));
    }, [thisMonthClientLevelData, trendPercent]);

    // Computed values for Monthly Performance card display
    // These change based on periodView (Current/Last Month toggle)
    // NOTE: Merchandise Insights always uses current month data (thisMonthClientLevelData)
    const displayedMonthData = React.useMemo(() => {
        if (!clientLevelData || clientLevelData.length === 0) {
            return null;
        }

        if (periodView === 'current') {
            return thisMonthClientLevelData;
        }

        // For last month view, we need to find the previous month's data
        const todayEpochMonth = EpochMonthService.getTodayEpochMonth();
        const lastMonthEpoch = todayEpochMonth - 1;
        return clientLevelData.find((item: ClientLevelItem) => item.epochMonthSpan === lastMonthEpoch) || null;
    }, [periodView, thisMonthClientLevelData, clientLevelData]);

    const displayedCompareData = React.useMemo(() => {
        if (!clientLevelData || clientLevelData.length === 0) {
            return null;
        }

        const todayEpochMonth = EpochMonthService.getTodayEpochMonth();

        if (periodView === 'current') {
            // Current month compares to last month
            const lastMonthEpoch = todayEpochMonth - 1;
            return clientLevelData.find((item: ClientLevelItem) => item.epochMonthSpan === lastMonthEpoch) || null;
        }

        // Last month compares to two months ago
        const twoMonthsAgoEpoch = todayEpochMonth - 2;
        return clientLevelData.find((item: ClientLevelItem) => item.epochMonthSpan === twoMonthsAgoEpoch) || null;
    }, [periodView, clientLevelData]);


    // Helper function to render trend indicator
    const renderTrendIndicator = (actual: number, plan: number, inverse: boolean = false, allowFlat: boolean = false) => {
        // For metrics where lower is better (like markdowns), set inverse = true
        // For metrics where higher is better (sales, inventory, inflow, otb), inverse = false
        // allowFlat = true for inventory (show flat when within 10% of plan)

        const difference = actual - plan;
        const percentDiff = plan !== 0 ? Math.abs(difference / plan) : 0;

        // Flat indicator for inventory when within 10% tolerance
        if (allowFlat && percentDiff <= 0.10) {
            return (
                <div className="flat-trend p-1.5 bg-gray-500/10 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="h-4 w-4 text-gray-500">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                </div>
            );
        }

        const isPositive = inverse ? difference < 0 : difference > 0;

        if (isPositive) {
            // Green up arrow
            return (
                <div className="up-trend p-1.5 bg-emerald-500/10 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="h-4 w-4 text-emerald-500">
                        <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
                        <polyline points="16 7 22 7 22 13"></polyline>
                    </svg>
                </div>
            );
        } else {
            // Red down arrow
            return (
                <div className="down-trend p-1.5 bg-red-500/10 rounded-lg">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                         className="h-4 w-4 text-red-500">
                        <polyline points="22 17 13.5 8.5 8.5 13.5 2 7"></polyline>
                        <polyline points="16 17 22 17 22 11"></polyline>
                    </svg>
                </div>
            );
        }
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="bg-gradient-to-r from-primary/5 to-accent/5 border-b border-border">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <h1 className="text-4xl font-bold text-foreground tracking-tight">
                        Performance Dashboard
                    </h1>
                    <p className="text-muted-foreground mt-2 text-lg">
                        Monitor your business performance and inventory insights
                    </p>
                </div>
            </div>

            {/* Main Content */}
            <div className="max-w-7xl mx-auto px-6 py-8">
                {hasMultipleClients && onToggleBack && (
                    <Button
                        onClick={() => {
                            onToggleBack();
                        }}
                        className="mb-4"
                        variant="outlined"
                    >
                        ← Back to All Clients View
                    </Button>
                )}

                <div className="dashboard-section">
                    {/* Merchandise Analysis Table Card */}
                    <div className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 dashboard-card border-0 shadow-lg">
                        <div className="px-6">
                            <div className="flex flex-col gap-6">
                                <div className="flex items-start justify-between">
                                    <div>
                                        <h2 className="text-2xl font-bold text-card-foreground tracking-tight">
                                        Monthly Performance
                                        </h2>
                                        <p className="text-muted-foreground mt-1">Track key metrics across time
                                            periods</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-muted-foreground">Location:</span>
                                            <MultiSelect
                                                options={locationOptions.map(loc => ({ code: loc.code, label: loc.label }))}
                                                value={selectedLocations}
                                                onChange={setSelectedLocations}
                                                placeholder="Select locations"
                                                className="w-48"
                                                allLabel="All Locations"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-muted-foreground">Category:</span>
                                            <MultiSelect
                                                options={categoryOptions
                                                    .filter(category => category.name && category.name.trim() !== '')
                                                    .map(cat => ({ code: cat.code, label: cat.name }))}
                                                value={selectedCategories}
                                                onChange={(newCategories: string[]) => {
                                                    setSelectedCategories(newCategories);
                                                    // Reset classes when categories change
                                                    setSelectedClasses([]);
                                                }}
                                                placeholder="Select categories"
                                                className="w-48"
                                                allLabel="All Categories"
                                            />
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-muted-foreground">Class:</span>
                                            <MultiSelect
                                                options={getFilteredClassOptions().map(cls => ({ code: cls.code, label: cls.label }))}
                                                value={selectedClasses}
                                                onChange={setSelectedClasses}
                                                placeholder="Select classes"
                                                className="w-64"
                                                allLabel="All Classes"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col gap-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex bg-gray-50 rounded-xl p-1.5 border border-border/50">
                                            <button
                                                onClick={() => setPeriodView('current')}
                                                className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                                                    periodView === 'current'
                                                        ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-sm'
                                                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                                                }`}
                                            >
                                                Current Month
                                            </button>
                                            <button
                                                onClick={() => setPeriodView('last')}
                                                className={`px-6 py-2.5 text-sm font-semibold rounded-lg transition-all duration-200 ${
                                                    periodView === 'last'
                                                        ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-sm'
                                                        : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                                                }`}
                                            >
                                                Last Month
                                            </button>
                                        </div>
                                    </div>
                                    <div
                                        className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border border-border/30">
                                        <div className="flex items-left gap-6">
                                            <div className="flex items-center gap-2">
                                                <span
                                                    className="text-xs font-medium text-muted-foreground">Value:</span>
                                                <div
                                                    className="flex bg-background/80 rounded-lg p-1 border border-border/50">
                                                    <button
                                                        onClick={() => setOtbValueMode('retail')}
                                                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                                                            otbValueMode === 'retail'
                                                                ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-sm'
                                                                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                                                        }`}
                                                    >
                                                        Retail
                                                    </button>
                                                    <button
                                                        onClick={() => setOtbValueMode('cost')}
                                                        className={`px-3 py-1.5 text-xs font-semibold rounded-md transition-all duration-200 ${
                                                            otbValueMode === 'cost'
                                                                ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-sm'
                                                                : 'text-muted-foreground hover:text-foreground hover:bg-background/50'
                                                        }`}
                                                    >
                                                        Cost
                                                    </button>
                                                </div>
                                            </div>
                                            <div className={"flex items-center gap-2"}>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-medium text-muted-foreground">Ending Period:</span>
                                                    <select
                                                        value={endingPeriod}
                                                        onChange={(e) => setEndingPeriod(Number(e.target.value))}
                                                        className="w-40 h-8 text-xs bg-background/80 border-border/50 rounded-md border px-3 py-2"
                                                    >
                                                        {endingPeriodOptions.map(option => (
                                                            <option key={option.value} value={option.value}>
                                                                {option.label}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            {/* Show Trend % Through Month only for Current Month view */}
                                            {periodView === 'current' && (
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className="text-xs font-medium text-muted-foreground">Trend % Through Month:
                                                    </span>
                                                    <input
                                                        type="text"
                                                        inputMode="decimal"
                                                        data-slot="input"
                                                        className="file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 flex min-w-0 rounded-md border px-3 py-1 shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive w-20 h-8 text-xs bg-background/80 border-border/50"
                                                        value={trendPercentInput}
                                                        onChange={(e) => setTrendPercentInput(e.target.value)}
                                                        onBlur={() => {
                                                            const next = Number.parseFloat(trendPercentInput);
                                                            if (Number.isFinite(next)) {
                                                                const clamped = Math.min(100, Math.max(0, next));
                                                                setTrendPercent(clamped);
                                                                setTrendPercentInput(clamped.toFixed(2));
                                                            } else {
                                                                setTrendPercentInput(trendPercent.toFixed(2));
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* KPI Cards */}
                        <div className="px-6 pt-0">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
                                {/* Sales Card */}
                                <div
                                    className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 shadow-sm dashboard-card hover:shadow-lg transition-all duration-200 border-0">
                                    <div className="px-6 flex flex-row items-center justify-between space-y-0 pb-3">
                                        <div
                                            className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                                            Sales
                                        </div>
                                        {/* Show trend indicator only for Current Month view */}
                                        {periodView === 'current' && displayedMonthData && renderTrendIndicator(
                                            displayedMonthData.salesRm,
                                            displayedMonthData.salesPlan,
                                            false // Higher sales is better
                                        )}
                                    </div>
                                    <div className="px-6 pt-0">
                                        {isLoading ? (
                                            <div className="text-muted-foreground">Loading...</div>
                                        ) : displayedMonthData ? (
                                            <>
                                                <div className="text-3xl font-bold text-card-foreground mb-2 tracking-tight">
                                                    {PerformanceDashboardService.moneyFormat(displayedMonthData.salesRm || 0)}
                                                </div>
                                                <div className="text-sm text-muted-foreground leading-relaxed">
                                                    Plan: {PerformanceDashboardService.moneyFormat(displayedMonthData.salesPlan || 0)}
                                                </div>
                                                {periodView === 'current' && (
                                                    <>
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                            Trend: {PerformanceDashboardService.moneyFormat(
                                                                trendPercent > 0
                                                                    ? displayedMonthData.salesRm / (trendPercent / 100)
                                                                    : displayedMonthData.salesRm
                                                            )}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                            Last Year: {PerformanceDashboardService.moneyFormat(displayedMonthData.salesLy || 0)}
                                                        </div>
                                                    </>
                                                )}
                                                {periodView === 'last' && (
                                                    <div className="text-sm text-muted-foreground leading-relaxed">
                                                        Last Year: {PerformanceDashboardService.moneyFormat(displayedMonthData.salesLy || 0)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-muted-foreground">No data available</div>
                                        )}
                                    </div>
                                </div>

                                {/* Markdowns Card */}
                                <div className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 shadow-sm dashboard-card hover:shadow-lg transition-all duration-200 border-0">
                                    <div className="px-6 flex flex-row items-center justify-between space-y-0 pb-3">
                                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                                            Markdowns
                                        </div>
                                        {/* Show trend indicator only for Current Month view */}
                                        {periodView === 'current' && displayedMonthData && renderTrendIndicator(
                                            displayedMonthData.markdownsRm,
                                            displayedMonthData.markdownsPlan,
                                            true // Lower markdowns is better
                                        )}
                                    </div>
                                    <div className="px-6 pt-0">
                                        {isLoading ? (
                                            <div className="text-muted-foreground">Loading...</div>
                                        ) : displayedMonthData ? (
                                            <>
                                                <div className="text-3xl font-bold text-card-foreground mb-2 tracking-tight">
                                                    {PerformanceDashboardService.moneyFormat(displayedMonthData.markdownsRm || 0)}
                                                </div>
                                                <div className="text-sm text-muted-foreground leading-relaxed">
                                                    Plan: {PerformanceDashboardService.moneyFormat(displayedMonthData.markdownsPlan || 0)}
                                                </div>
                                                {periodView === 'last' && (
                                                    <div className="text-sm text-muted-foreground leading-relaxed">
                                                        Last Year: {PerformanceDashboardService.moneyFormat(displayedMonthData.markdownsLy || 0)}
                                                    </div>
                                                )}
                                                {periodView === 'current' && (
                                                    <div className="text-sm text-muted-foreground leading-relaxed">
                                                        LY: {PerformanceDashboardService.moneyFormat(displayedMonthData.markdownsLy || 0)}
                                                    </div>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-muted-foreground">No data available</div>
                                        )}
                                    </div>
                                </div>

                                {/* Current Inventory Card */}
                                <div className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 shadow-sm dashboard-card hover:shadow-lg transition-all duration-200 border-0">
                                    <div className="px-6 flex flex-row items-center justify-between space-y-0 pb-3">
                                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                                            {periodView === 'last' ? 'Inventory' : 'Current Inventory'}
                                        </div>
                                        {/* Show trend indicator only for Current Month view */}
                                        {periodView === 'current' && displayedMonthData && renderTrendIndicator(
                                            displayedMonthData.inventoryCurrent || 0,
                                            displayedMonthData.inventoryBomPlanNM || 0,
                                            false, // Higher inventory meeting plan is good
                                            true   // Allow flat indicator within 10%
                                        )}
                                    </div>
                                    <div className="px-6 pt-0">
                                        {isLoading ? (
                                            <div className="text-muted-foreground">Loading...</div>
                                        ) : displayedMonthData ? (
                                            <>
                                                <div className="text-3xl font-bold text-card-foreground mb-2 tracking-tight">
                                                    {PerformanceDashboardService.moneyFormat(
                                                        periodView === 'current'
                                                            ? (displayedMonthData.inventoryCurrent || 0)
                                                            : (displayedMonthData.inventoryBOMRM || 0)
                                                    )}
                                                </div>
                                                {periodView === 'last' && (
                                                    <>
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                            BOM Plan: {PerformanceDashboardService.moneyFormat(displayedMonthData.inventoryBOMPlan || 0)}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                             BOM LY: {PerformanceDashboardService.moneyFormat(displayedMonthData.inventoryBomLy || 0)}
                                                        </div>
                                                    </>
                                                )}
                                                {periodView === 'current' && (
                                                    <>
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                            {nextMonthAbbr} Plan: {PerformanceDashboardService.moneyFormat(displayedMonthData.inventoryBomPlanNM || 0)}
                                                        </div>
                                                        {/*<div className="text-sm text-muted-foreground leading-relaxed">*/}
                                                        {/*    {currentMonthAbbr} Trend: {PerformanceDashboardService.moneyFormat(computedInventoryTrend)}*/}
                                                        {/*</div>*/}
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                            LY: {PerformanceDashboardService.moneyFormat(displayedMonthData.inventoryEomLy || 0)}
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-muted-foreground">No data available</div>
                                        )}
                                    </div>
                                </div>

                                {/* Inflow Card */}
                                <div className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 shadow-sm dashboard-card hover:shadow-lg transition-all duration-200 border-0">
                                    <div className="px-6 flex flex-row items-center justify-between space-y-0 pb-3">
                                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                                            Inflow
                                        </div>
                                        {/* Show trend indicator only for Current Month view */}
                                        {periodView === 'current' && displayedMonthData && renderTrendIndicator(
                                            displayedMonthData.inflowRetailRm,
                                            displayedMonthData.inflowPlanRetailRm,
                                            false // Higher inflow (meeting/exceeding plan) is better
                                        )}
                                    </div>
                                    <div className="px-6 pt-0">
                                        {isLoading ? (
                                            <div className="text-muted-foreground">Loading...</div>
                                        ) : displayedMonthData ? (
                                            <>
                                                <div className="text-3xl font-bold text-card-foreground mb-2 tracking-tight">
                                                    {PerformanceDashboardService.moneyFormat(displayedMonthData.inflowRetailRm || 0)}
                                                </div>
                                                <div className="text-sm text-muted-foreground leading-relaxed">
                                                    Plan: {PerformanceDashboardService.moneyFormat(displayedMonthData.inflowPlanRetailRm || 0)}
                                                </div>
                                                {periodView === 'last' && (
                                                    <div className="text-sm text-muted-foreground leading-relaxed">
                                                        Last Year: {PerformanceDashboardService.moneyFormat(displayedMonthData.inflowRetailLy || 0)}
                                                    </div>
                                                )}
                                                {periodView === 'current' && (
                                                    <>
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                            Trend: {PerformanceDashboardService.moneyFormat(computedInflowTrend)}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground leading-relaxed">
                                                            LY: {PerformanceDashboardService.moneyFormat(displayedMonthData.inflowRetailLy || 0)}
                                                        </div>
                                                    </>
                                                )}
                                            </>
                                        ) : (
                                            <div className="text-muted-foreground">No data available</div>
                                        )}
                                    </div>
                                </div>

                                {/* Open to Buy Card */}
                                <div className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 shadow-sm dashboard-card hover:shadow-lg transition-all duration-200 border-0">
                                    <div className="px-6 flex flex-row items-center justify-between space-y-0 pb-3">
                                        <div className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                                            Open to Buy ({otbValueMode === 'cost' ? 'Cost' : 'Retail'})
                                        </div>
                                        {/* NO TREND INDICATOR FOR OTB per requirements */}
                                    </div>
                                    <div className="px-6 pt-0">
                                        {isLoading ? (
                                            <div className="text-muted-foreground">Loading...</div>
                                        ) : clientLevelData ? (
                                            <>
                                                <div className="text-3xl font-bold text-card-foreground mb-2 tracking-tight">
                                                    {PerformanceDashboardService.moneyFormat(
                                                        otbValueMode === 'cost' ? computedOtbValues.cost : computedOtbValues.retail
                                                    )}
                                                </div>
                                                <div className="text-sm text-muted-foreground leading-relaxed">
                                                    Through {endingPeriodLabel}
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-muted-foreground">No data available</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Inventory Health Table Card */}
                    <div className="bg-card text-card-foreground flex flex-col gap-6 rounded-xl py-6 dashboard-card border-0 shadow-lg mt-[30px]">
                        <div className="px-6 pb-6">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-2xl font-bold text-card-foreground tracking-tight">
                                        Merchandise Insights
                                    </h2>
                                    <p className="text-muted-foreground mt-1">Analyze performance across different
                                        classes</p>
                                </div>
                                <select
                                    value={tableLimit}
                                    onChange={(e) => setTableLimit(Number(e.target.value))}
                                    className="w-40 h-9 text-sm bg-background border-border/50 rounded-md border px-3 py-2"
                                >
                                    <option value={5}>Top 5 Items</option>
                                    <option value={10}>Top 10 Items</option>
                                    <option value={15}>Top 15 Items</option>
                                    <option value={20}>Top 20 Items</option>
                                </select>
                            </div>

                            <div className="flex space-x-2 bg-gray-200 gap-2 w-full rounded-xl border mb-4">
                                <button
                                    onClick={() => setInventoryView('buying')}
                                    className={`flex-1 font-semibold transition-all duration-200 h-8 rounded-md px-3 ${
                                        inventoryView === 'buying'
                                            ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-sm'
                                            : 'hover:bg-background/50'
                                    }`}
                                >
                                    Buying Opportunity
                                </button>
                                <button
                                    onClick={() => setInventoryView('fastest')}
                                    className={`flex-1 font-semibold transition-all duration-200 h-8 rounded-md px-3 ${
                                        inventoryView === 'fastest'
                                            ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-sm'
                                            : 'hover:bg-background/50'
                                    }`}
                                >
                                    Fastest Sell-Through
                                </button>
                                <button
                                    onClick={() => setInventoryView('slowest')}
                                    className={`flex-1 font-semibold transition-all duration-200 h-8 rounded-md px-3 ${
                                        inventoryView === 'slowest'
                                            ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-sm'
                                            : 'hover:bg-background/50'
                                    }`}
                                >
                                    Slowest Sell-Through
                                </button>
                            </div>
                            <div>
                                <button
                                    onClick={() => setShowInventoryActionReport(true)}
                                    className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:text-primary/80 transition-colors bg-transparent border-0 cursor-pointer p-0"
                                >
                                    Inventory Action Report
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                         fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                                         strokeLinejoin="round" className="lucide lucide-external-link h-3.5 w-3.5">
                                        <path d="M15 3h6v6"></path>
                                        <path d="M10 14 21 3"></path>
                                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                                    </svg>
                                </button>
                            </div>
                            <div
                                className="flex flex-wrap items-center gap-4 mb-4 p-4 bg-gray-200 rounded-lg border border-border/30">
                                {/* Show Beginning Period dropdown only for Fastest/Slowest views */}
                                {(inventoryView === 'fastest' || inventoryView === 'slowest') && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-foreground">Beginning Period:</span>
                                        <select
                                            value={beginningPeriod}
                                            onChange={(e) => setBeginningPeriod(Number(e.target.value))}
                                            className="w-40 h-9 text-sm bg-background border-border/50 rounded-md border px-3 py-2"
                                        >
                                            {beginningPeriodOptions.map(option => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                )}
                                {/* Value Mode only shown for Buying Opportunity view, affects only On Order and OTB columns */}
                                {inventoryView === 'buying' && (
                                    <div className="flex items-center gap-2">
                                        <span className="text-sm font-medium text-foreground">Value Mode (On Order & OTB):</span>
                                        <div className="flex bg-background rounded-lg border border-border/50 p-1">
                                            <button
                                                onClick={() => setValueMode('retail')}
                                                className={`h-8 px-3 text-xs font-medium rounded-md transition-all ${
                                                    valueMode === 'retail'
                                                        ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-xs'
                                                        : 'hover:bg-accent hover:text-accent-foreground'
                                                }`}
                                            >
                                                Retail
                                            </button>
                                            <button
                                                onClick={() => setValueMode('cost')}
                                                className={`h-8 px-3 text-xs font-medium rounded-md transition-all ${
                                                    valueMode === 'cost'
                                                        ? 'bg-purple-900 text-white text-sm font-semibold rounded-md hover:bg-purple-800 transition-colors shadow-xs'
                                                        : 'hover:bg-accent hover:text-accent-foreground'
                                                }`}
                                            >
                                                Cost
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="px-6 pt-0">
                            <div className="overflow-x-auto rounded-lg border border-border/50">
                                <table className="w-full">
                                    {inventoryView === 'buying' ? (
                                        // Buying Opportunity Table
                                        <>
                                            <thead className="bg-gray-300">
                                            <tr>
                                                    <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Class</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Sales (Retail)</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Markdowns (Retail)</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Inventory (Retail)</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Inventory Trend (Retail)</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Inventory Plan (Retail)</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">
                                                        On Order ({valueMode === 'cost' ? 'Cost' : 'Retail'})
                                                        <div className="text-xs text-muted-foreground font-normal mt-1">Through {endingPeriodLabel}</div>
                                                    </th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">
                                                        Open to Buy Trend ({valueMode === 'cost' ? 'Cost' : 'Retail'})
                                                        <div className="text-xs text-muted-foreground font-normal mt-1">Through {endingPeriodLabel}</div>
                                                    </th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoadingInventoryHealth ? (
                                                    <tr>
                                                        <td colSpan={8} className="py-8 px-4 text-center text-muted-foreground">
                                                            Loading inventory health data...
                                                        </td>
                                                    </tr>
                                                ) : inventoryHealthData.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={8} className="py-8 px-4 text-center text-muted-foreground">
                                                            No data available
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    inventoryHealthData.map((row, index: number) => {
                                                        // For On Order and OTB Trend, use valueMode; everything else is always retail
                                                        return (
                                                            <tr key={index} className="border-b border-border/30 hover:bg-gray-200 transition-colors duration-150">
                                                                <td className="py-4 px-4 text-sm font-medium text-card-foreground">
                                                                    <div>{row.classCode} - {row.className}</div>
                                                                    {row.category && <div className="text-xs text-muted-foreground">{row.category}</div>}
                                                                    {row.locationCode && <div className="text-xs text-muted-foreground opacity-60 mt-1">[{row.locationCode}{row.locationName ? ` - ${row.locationName}` : ''}]</div>}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.salesRetail || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.markdownsRetail || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.inventoryRetail || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.inventoryTrendRetail || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.inventoryPlanRetail || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(valueMode === 'cost' ? (row.onOrderCost || 0) : (row.onOrderRetail || 0))}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(valueMode === 'cost' ? (row.otbCost || 0) : (row.otbRetail || 0))}
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </>
                                    ) : (
                                        // Fastest/Slowest Sell-Through Table
                                        <>
                                            <thead className="bg-gray-300">
                                                <tr>
                                                    <th className="text-left py-4 px-4 text-sm font-semibold text-foreground">Class</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Sales</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">Inflow Retail</th>
                                                    <th className="text-right py-4 px-4 text-sm font-semibold text-foreground">
                                                        Beginning Inventory
                                                        <div className="text-xs text-muted-foreground font-normal mt-1">
                                                            From {beginningPeriodOptions.find(opt => opt.value === beginningPeriod)?.label || 'Unknown'}
                                                        </div>
                                                    </th>
                                                    <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">MMU %</th>
                                                    <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">Sell-Through</th>
                                                    <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">Comp to LY</th>
                                                    <th className="text-center py-4 px-4 text-sm font-semibold text-foreground">Sell-Through Goal</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoadingInventoryHealth ? (
                                                    <tr>
                                                        <td colSpan={8} className="py-8 px-4 text-center text-muted-foreground">
                                                            Loading sell-through data...
                                                        </td>
                                                    </tr>
                                                ) : inventoryHealthData.length === 0 ? (
                                                    <tr>
                                                        <td colSpan={8} className="py-8 px-4 text-center text-muted-foreground">
                                                            No data available
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    inventoryHealthData.map((row, index: number) => {
                                                        // No retail/cost control for Fastest/Slowest tables - always use retail values
                                                        const sellThrough = row.sellThroughRate || 0;
                                                        const mmu = (row.mmuPercent || 0) * 100; // Multiply by 100 to convert decimal to percentage
                                                        const compToLy = row.compToLyPercent || 0;
                                                        const sellThroughGoal = row.sellThroughGoal || 0;

                                                        return (
                                                            <tr key={index} className="border-b border-border/30 hover:bg-gray-200 transition-colors duration-150">
                                                                <td className="py-4 px-4 text-sm font-medium text-card-foreground">
                                                                    <div>{row.classCode} - {row.className}</div>
                                                                    {row.category && <div className="text-xs text-muted-foreground">{row.category}</div>}
                                                                    {row.locationCode && <div className="text-xs text-muted-foreground opacity-60 mt-1">[{row.locationCode}{row.locationName ? ` - ${row.locationName}` : ''}]</div>}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.sales || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.inflowRetail || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-right font-semibold text-card-foreground">
                                                                    {PerformanceDashboardService.moneyFormat(row.beginningInventory || 0)}
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-center font-semibold text-card-foreground">
                                                                    <span className={mmu > 15 ? 'text-red-500' : mmu > 10 ? 'text-yellow-500' : 'text-emerald-500'}>
                                                                        {mmu.toFixed(1)}%
                                                                    </span>
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-center font-semibold">
                                                                    <span className={sellThrough > 80 ? 'text-emerald-500' : sellThrough > 60 ? 'text-yellow-500' : 'text-red-500'}>
                                                                        {sellThrough.toFixed(1)}%
                                                                    </span>
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-center font-semibold text-card-foreground">
                                                                    <span className={compToLy > 0 ? 'text-emerald-500' : compToLy < 0 ? 'text-red-500' : ''}>
                                                                        {compToLy > 0 ? '+' : ''}{compToLy.toFixed(1)}%
                                                                    </span>
                                                                </td>
                                                                <td className="py-4 px-4 text-sm text-center font-semibold text-muted-foreground">
                                                                    {sellThroughGoal.toFixed(1)}%
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                                )}
                                            </tbody>
                                        </>
                                    )}
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Inventory Action Report Modal */}
            <Dialog
                open={showInventoryActionReport}
                onClose={() => setShowInventoryActionReport(false)}
                maxWidth="xl"
                fullWidth
            >
                <DialogTitle>
                    Inventory Action Report
                    <IconButton
                        aria-label="close"
                        onClick={() => setShowInventoryActionReport(false)}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18"></line>
                            <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {currentClientContext.client?.Id && (
                        <InventoryActionReport
                            clientId={currentClientContext.client.Id}
                            epochMonth={epochMonth}
                        />
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default ClientProgress;


